import * as React from 'react';
import { PieChart, PieSeries } from '@mui/x-charts/PieChart';
import { Paper, Stack, Skeleton, Box } from '@mui/material';
import { ILabelValuePanel, IPieChartPanel } from '../Dashboard.model';
import { useMemo } from 'react';
import { LayoutItem } from 'react-grid-layout';
import LabelValuePanel from '../LabelValuePanel/LabelValuePanel';
import styled from '@emotion/styled';
import { useDrawingArea } from '@mui/x-charts';
interface PieChartPanelProps {
  panel: IPieChartPanel;
  loading?: boolean;
  layout?: LayoutItem | undefined;
}

const StyledText = styled('text')<{ fontSize?: number | string }>(
  ({ fontSize }) => ({
    fill: '#fff',
    textAnchor: 'middle',
    dominantBaseline: 'central',
    fontSize,
  })
);
function PieCenterLabel({
  children,
}: {
  children: React.ReactNode;
  fontSize?: number | string;
}) {
  const { width, height } = useDrawingArea();
  const numericInnerRadius = (Math.min(width, height) * 0.6) / 2;
  const size = numericInnerRadius * 0.8;

  return (
    <StyledText x="50%" y="70%" style={{ fontSize: size }}>
      {children}
    </StyledText>
  );
}
export function PieChartPanel(props: PieChartPanelProps) {
  const { panel, loading, layout } = props;
  const { icon } = panel;

  const loadingState = loading ?? panel.loading;

  const isSmall = layout ? layout?.w <= layout.h && layout.w <= 2 : false;
  const value = useMemo(
    () =>
      panel.series.reduce(
        (acc: number, item: PieSeries) =>
          acc +
          item.data.reduce(
            (sum: number, d: { value: number }) => sum + d.value,
            0
          ),
        0
      ),
    [panel.series]
  );

  const chart = (
    <Box
      sx={{
        aspectRatio: '2 / 1',
        width: '100%',
        height: '100%',
      }}
    >
      <PieChart
        series={panel.series}
        margin={0}
        sx={{
          width: '100%',
        }}
        hideLegend={isSmall || !!icon}
        slotProps={{
          legend: {
            direction: 'horizontal',
            position: {
              vertical: 'bottom',
              horizontal: 'center',
            },
          },
        }}
      >
        <PieCenterLabel fontSize={panel.series[0].innerRadius ?? '24px'}>
          {value}
        </PieCenterLabel>
      </PieChart>
    </Box>
  );
  const content = isSmall ? (
    chart
  ) : (
    <Stack
      direction="column"
      alignItems="center"
      justifyContent="space-between"
      display={'flex'}
      height={'100%'}
      pb={1}
    >
      <Paper
        elevation={8}
        sx={{
          height: '100%',
          maxWidth: '100%',
          flex: 1,
          pt: 1,
          width: '100%',
          boxShadow: 'none',
        }}
      >
        {loadingState ? (
          <Skeleton variant="rectangular" width="100%" height={'100%'} />
        ) : (
          chart
        )}
      </Paper>
    </Stack>
  );

  const labelPanel: ILabelValuePanel = {
    ...panel,
    type: 'LABEL_VALUE',
    content: content,
  };

  return <LabelValuePanel panel={labelPanel} layout={layout} />;
}

export default PieChartPanel;
